import { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, Users as UsersIcon, ShieldCheck, KeyRound, Copy, X, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Badge, PageHeader, SlideOver, Field, Empty } from "@/components/UI";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const ROLES = ["member", "manager", "admin", "customer"];
const ROLE_HINT = {
  member:   "Standard workspace access — bids, DMS, tickets, feeds.",
  manager:  "Same as member + can update team members' roles.",
  admin:    "Full access — team, feeds, backups, all settings.",
  customer: "Restricted access — the client can ONLY see the Ticket portal and their own tickets. Forced password change on first login.",
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [invite, setInvite] = useState({
    open: false,
    email: "",
    full_name: "",
    role: "member",
    company_name: "",
    phone: "",
  });
  const { user } = useAuth();
  const { toast } = useToast();
  const [filter, setFilter] = useState("all"); // all | pending | inactive
  const [selected, setSelected] = useState(new Set());
  // Dialog shown after an invite so the admin can copy the temp password
  // in case the credential email fails to deliver.
  const [creds, setCreds] = useState(null);

  const filtered = members.filter((m) => {
    if (filter === "pending") return m.pending_invite && !m.last_login_at;
    if (filter === "inactive") return !m.is_active;
    return true;
  });

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const selectAllVisible = () => {
    const ids = filtered
      .filter((m) => m.id !== user?.id && m.role !== "admin")
      .map((m) => m.id);
    setSelected(new Set(ids));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const list = members.filter((m) => selected.has(m.id));
    if (!window.confirm(
      `Delete ${list.length} account(s)?\n\n${list.map((m) => `· ${m.email}`).join("\n")}\n\nThis cannot be undone.`
    )) return;
    let ok = 0;
    let fail = 0;
    for (const m of list) {
      try {
        await api.deleteMember(m.id);
        ok++;
      } catch {
        fail++;
      }
    }
    toast(`Deleted ${ok}${fail ? ` · ${fail} failed` : ""}`, fail ? "info" : "success");
    clearSelection();
    load();
  };

  const load = useCallback(
    () => api.team().then(setMembers).catch(() => toast("Failed to load", "error")),
    [toast],
  );
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!invite.email) { toast("Email required", "error"); return; }
    try {
      if (invite.role === "customer") {
        const u = await api.inviteCustomer(invite.email, invite.full_name, invite.company_name, invite.phone);
        // Always surface the temp password + delivery status to the admin.
        setCreds({
          email: u.email,
          temp_password: u.temp_password || "(not returned — check email)",
          email_sent: u.email_sent,
          message: u.message,
          role: "customer",
        });
      } else {
        const u = await api.invite({
          email: invite.email,
          full_name: invite.full_name,
          role: invite.role,
          phone: invite.phone,
        });
        setCreds({
          email: u.email,
          temp_password: u.temp_password || "(not returned — check email)",
          email_sent: u.email_sent,
          message: u.message,
          role: invite.role,
        });
      }
      setInvite({ open: false, email: "", full_name: "", role: "member", company_name: "", phone: "" });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Invite failed", "error");
    }
  };

  const changeRole = async (m, role) => {
    if (role === m.role) return;
    try { await api.updateMember(m.id, { role }); toast("Role updated", "success"); load(); }
    catch { toast("Update failed", "error"); }
  };

  const toggle = async (m) => {
    try { await api.updateMember(m.id, { is_active: !m.is_active }); load(); }
    catch { toast("Update failed", "error"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.email}?`)) return;
    try { await api.deleteMember(m.id); toast("Deleted", "success"); load(); }
    catch { toast("Delete failed", "error"); }
  };

  const [resetDlg, setResetDlg] = useState({ open: false, member: null, running: false, result: null });

  const resetPassword = async () => {
    if (!resetDlg.member) return;
    setResetDlg((s) => ({ ...s, running: true }));
    try {
      const res = await api.resetMemberPassword(resetDlg.member.id, { send_email: true });
      setResetDlg((s) => ({ ...s, running: false, result: res }));
      toast(res.message, res.email_sent ? "success" : "info");
    } catch (e) {
      setResetDlg((s) => ({ ...s, running: false }));
      toast(e?.response?.data?.detail || "Reset failed", "error");
    }
  };

  const copyTemp = () => {
    if (!resetDlg.result?.temp_password) return;
    navigator.clipboard.writeText(resetDlg.result.temp_password);
    toast("Copied to clipboard", "success");
  };

  return (
    <div data-testid="page-team">
      <PageHeader
        icon={UsersIcon}
        title="Team"
        subtitle={`${members.length} member${members.length === 1 ? "" : "s"}`}
        action={
          user?.role === "admin" && (
            <button
              data-testid="invite-btn"
              onClick={() => setInvite({ ...invite, open: true })}
              className="btn btn-primary"
            >
              <UserPlus size={14} /> Invite
            </button>
          )
        }
      />

      <div className="card overflow-hidden">
        {/* Filter + bulk actions toolbar (admin only) */}
        {user?.role === "admin" && members.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 text-xs muted">
              <Filter size={12} /> Filter
            </div>
            {[
              ["all", `All (${members.length})`],
              ["pending", `Pending (${members.filter((m) => m.pending_invite && !m.last_login_at).length})`],
              ["inactive", `Inactive (${members.filter((m) => !m.is_active).length})`],
            ].map(([k, label]) => (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => { setFilter(k); clearSelection(); }}
                className={`text-xs px-3 py-1 rounded-full transition ${
                  filter === k
                    ? "bg-[#0e6939] text-white font-semibold"
                    : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
            <div className="flex-1" />
            {selected.size > 0 && (
              <>
                <span className="text-xs muted" data-testid="selection-count">
                  {selected.size} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="btn btn-ghost btn-sm"
                  data-testid="bulk-clear"
                >
                  Clear
                </button>
                <button
                  onClick={bulkDelete}
                  className="btn btn-sm text-white"
                  style={{ background: "#b91c1c" }}
                  data-testid="bulk-delete"
                >
                  <Trash2 size={12} /> Delete {selected.size}
                </button>
              </>
            )}
            {selected.size === 0 && filtered.length > 1 && filter !== "all" && (
              <button
                onClick={selectAllVisible}
                className="btn btn-ghost btn-sm"
                data-testid="select-all-visible"
              >
                Select all visible
              </button>
            )}
          </div>
        )}

        {members.length === 0 ? (
          <Empty icon={UsersIcon} title="No team members yet" />
        ) : filtered.length === 0 ? (
          <Empty icon={UsersIcon} title="No accounts match this filter" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                {user?.role === "admin" && <th style={{ width: 32 }}></th>}
                <th>Member</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} data-testid={`member-${m.id}`}>
                  {user?.role === "admin" && (
                    <td>
                      {m.id !== user.id && m.role !== "admin" && (
                        <input
                          type="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggleSelect(m.id)}
                          data-testid={`select-${m.id}`}
                          aria-label={`Select ${m.email}`}
                        />
                      )}
                    </td>
                  )}
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="avatar avatar-sm">
                        {(m.full_name || m.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold">{m.full_name || m.email}</div>
                        <div className="text-xs muted">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {user?.role === "admin" && m.id !== user.id ? (
                      <select
                        data-testid={`role-${m.id}`}
                        className="select max-w-[140px] text-xs py-1"
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value)}
                      >
                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
                        {m.role === "admin" && <ShieldCheck size={12} style={{ color: "var(--green-700)" }} />}
                        {m.role}
                      </span>
                    )}
                  </td>
                  <td>
                    {m.is_active ? <Badge tone="emerald" dot>Active</Badge> : <Badge tone="slate">Inactive</Badge>}
                    {m.pending_invite && <Badge tone="amber">Pending</Badge>}
                  </td>
                  <td className="muted">{formatDate(m.created_at)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {user?.role === "admin" && m.id !== user.id && (
                      <>
                        <button
                          data-testid={`toggle-${m.id}`}
                          onClick={() => toggle(m)}
                          className="text-xs muted hover:text-[#0e6939] mr-3"
                        >
                          {m.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          data-testid={`reset-${m.id}`}
                          onClick={() => setResetDlg({ open: true, member: m, running: false, result: null })}
                          className="text-xs muted hover:text-[#0e6939] mr-3 inline-flex items-center gap-1"
                          title="Reset this user's password"
                        >
                          <KeyRound size={12} /> Reset password
                        </button>
                        <button
                          data-testid={`del-${m.id}`}
                          onClick={() => remove(m)}
                          className="text-slate-500 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SlideOver
        open={invite.open}
        narrow
        onClose={() => setInvite({ ...invite, open: false })}
        title="Invite user"
        subtitle="Team members get full workspace access. Customers only see the Ticket portal."
        footer={
          <>
            <button
              onClick={() => setInvite({ ...invite, open: false })}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              className="btn btn-primary"
              data-testid="submit-invite"
            >
              Send invite
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Role *">
            <select
              data-testid="invite-role"
              className="select"
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r === "customer" ? "customer (external client — Tickets only)" : r}
                </option>
              ))}
            </select>
            <div className="text-[11px] muted mt-2">{ROLE_HINT[invite.role]}</div>
          </Field>
          <Field label="Full name">
            <input
              data-testid="invite-name"
              className="input"
              value={invite.full_name}
              onChange={(e) => setInvite({ ...invite, full_name: e.target.value })}
            />
          </Field>
          <Field label="Email *">
            <input
              data-testid="invite-email"
              required
              type="email"
              className="input"
              placeholder={
                invite.role === "customer"
                  ? "e.g. enachonga@pridebank.co.ug"
                  : "e.g. jsmith@jero-onetechnologies.com"
              }
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            />
          </Field>
          {invite.role === "customer" && (
            <Field label="Company / Organisation">
              <input
                data-testid="invite-company"
                className="input"
                placeholder="e.g. Pride Bank Uganda Ltd"
                value={invite.company_name}
                onChange={(e) =>
                  setInvite({ ...invite, company_name: e.target.value })
                }
              />
            </Field>
          )}
          <Field label="Phone (for WhatsApp / future SMS)">
            <input
              data-testid="invite-phone"
              type="tel"
              className="input"
              placeholder="+256 7xx xxx xxx"
              value={invite.phone}
              onChange={(e) => setInvite({ ...invite, phone: e.target.value })}
            />
            <div className="text-[11px] muted mt-1">
              Used to send ticket updates via WhatsApp once we enable it.
            </div>
          </Field>
          <div
            className="text-[11px] muted rounded p-3"
            style={{ background: "var(--green-50)", color: "var(--ink-700)" }}
          >
            {invite.role === "customer" ? (
              <>
                A random temporary password is generated and{" "}
                <b>emailed to the customer</b> via your connected Gmail. They
                will be forced to change it on first login and can only see
                the Ticket portal.
              </>
            ) : (
              <>
                A random temporary password is generated and shown to you once
                (also sent via <b>your connected Gmail</b>). The user must
                change it on first login.
              </>
            )}
          </div>
        </div>
      </SlideOver>

      {/* Admin password reset modal */}
      {resetDlg.open && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="reset-password-modal"
          onClick={(e) => e.target === e.currentTarget && !resetDlg.running && setResetDlg({ open: false, member: null, running: false, result: null })}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <KeyRound size={16} style={{ color: "var(--green-700)" }} />
                <h3 className="font-bold">Reset user password</h3>
              </div>
              <button
                onClick={() => !resetDlg.running && setResetDlg({ open: false, member: null, running: false, result: null })}
                className="btn btn-ghost btn-icon"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!resetDlg.result ? (
                <>
                  <p className="text-sm">
                    Reset the password for <b>{resetDlg.member?.email}</b>?
                  </p>
                  <ul className="text-xs muted list-disc pl-4 space-y-1">
                    <li>A secure random temporary password will be generated.</li>
                    <li>The user will be <b>forced</b> to change it on next login.</li>
                    <li>An email with the temporary password is sent to the user (via Gmail, with SMTP fallback).</li>
                    <li>You&apos;ll also see the temp password here — copy it and share via a secure channel as a backup.</li>
                  </ul>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setResetDlg({ open: false, member: null, running: false, result: null })}
                      className="btn btn-ghost"
                      disabled={resetDlg.running}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={resetPassword}
                      className="btn btn-primary"
                      disabled={resetDlg.running}
                      data-testid="reset-confirm"
                    >
                      {resetDlg.running ? "Resetting…" : "Reset password"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg border border-[#0e6939] bg-[#f3faf4] p-3">
                    <div className="text-xs muted mb-1">Temporary password</div>
                    <div className="flex items-center justify-between gap-3">
                      <code className="text-base font-bold tracking-wider" data-testid="temp-password">
                        {resetDlg.result.temp_password}
                      </code>
                      <button
                        onClick={copyTemp}
                        className="btn btn-ghost btn-sm"
                        data-testid="copy-temp"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                  </div>
                  <div className="text-xs">
                    {resetDlg.result.email_sent ? (
                      <span style={{ color: "var(--green-700)" }}>
                        ✓ Email sent to <b>{resetDlg.result.user_email}</b>.
                      </span>
                    ) : (
                      <span className="text-amber-700">
                        ⚠ Email delivery failed. Share the temp password with
                        <b> {resetDlg.result.user_email}</b> manually (e.g. via SMS).
                      </span>
                    )}
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => setResetDlg({ open: false, member: null, running: false, result: null })}
                      className="btn btn-primary"
                      data-testid="reset-done"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post-invite credentials dialog — shown after admin invites a member
          or customer, so temp password is visible even if Gmail delivery fails. */}
      {creds && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="invite-creds-modal"
          onClick={(e) => e.target === e.currentTarget && setCreds(null)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <UserPlus size={16} style={{ color: "var(--green-700)" }} />
                <h3 className="font-bold">Invite created</h3>
              </div>
              <button
                onClick={() => setCreds(null)}
                className="btn btn-ghost btn-icon"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm">
                <b>{creds.email}</b> has been added as a{" "}
                <span className="font-semibold" style={{ color: "var(--green-700)" }}>{creds.role}</span>.
              </div>

              <div className="rounded-lg border border-[#0e6939] bg-[#f3faf4] p-3">
                <div className="text-xs muted mb-1">Temporary password (share via secure channel)</div>
                <div className="flex items-center justify-between gap-3">
                  <code className="text-base font-bold tracking-wider" data-testid="invite-temp-password">
                    {creds.temp_password}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(creds.temp_password);
                      toast("Copied to clipboard", "success");
                    }}
                    className="btn btn-ghost btn-sm"
                    data-testid="invite-copy-temp"
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
              </div>

              <div className="text-xs">
                {creds.email_sent === true ? (
                  <span style={{ color: "var(--green-700)" }}>
                    ✓ Credentials email sent to <b>{creds.email}</b> via Gmail.
                  </span>
                ) : creds.email_sent === false ? (
                  <span className="text-amber-700">
                    ⚠ Email delivery failed (Gmail not connected or send error).
                    Share the temp password with <b>{creds.email}</b> manually
                    via SMS, WhatsApp, or a phone call.
                  </span>
                ) : (
                  <span className="text-slate-600">
                    {creds.message}
                  </span>
                )}
              </div>

              <ul className="text-xs muted list-disc pl-4 space-y-1">
                <li>User will be forced to change this password on first login.</li>
                <li>Login URL: <code>/login</code> — same page for team and customers.</li>
              </ul>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setCreds(null)}
                  className="btn btn-primary"
                  data-testid="invite-creds-done"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
